// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Simple Swiper-like functionality for social media highlights
class SimpleSwiper {
  constructor(element, options = {}) {
    this.element = element;
    this.wrapper = element.querySelector('.swiper-wrapper');
    this.slides = [...element.querySelectorAll('.swiper-slide')];
    this.options = {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      autoplay: false,
      speed: 5000,
      breakpoints: {},
      navigation: {},
      on: {},
      ...options
    };
    
    this.currentIndex = 0;
    this.isAutoplay = false;
    this.autoplayInterval = null;
    this.isMouseOver = false;
    this.hasClonedSlides = false;
    this.currentTranslateX = 0;
    this.animationStarted = false;
    
    this.init();
  }
  
  init() {
    this.setupStyles();
    this.setupNavigation();
    this.setupAutoplay();
    this.setupResponsive();
    this.update();
    
    // Trigger afterInit callback
    if (this.options.on.afterInit) {
      this.options.on.afterInit();
    }
  }
  
  setupStyles() {
    this.element.style.position = 'relative';
    this.element.style.overflow = 'hidden';
    
    this.wrapper.style.display = 'flex';
    this.wrapper.style.transition = `transform ${this.options.speed}ms ease`;
    this.wrapper.style.willChange = 'transform';
    
    this.slides.forEach((slide, index) => {
      slide.style.flexShrink = '0';
      slide.style.position = 'relative';
    });
  }
  
  setupNavigation() {
    if (this.options.navigation.prevEl) {
      const prevBtn = document.querySelector(this.options.navigation.prevEl);
      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.slidePrev());
      }
    }
    
    if (this.options.navigation.nextEl) {
      const nextBtn = document.querySelector(this.options.navigation.nextEl);
      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.slideNext());
      }
    }
  }
  
  setupAutoplay() {
    if (this.options.autoplay && this.options.autoplay.delay === 0) {
      this.isAutoplay = true;
      this.startContinuousAutoplay();
      
      // Pause on hover if enabled
      if (this.options.autoplay.pauseOnMouseEnter) {
        this.element.addEventListener('mouseenter', () => {
          this.isMouseOver = true;
          this.pauseAutoplay();
        });
        
        this.element.addEventListener('mouseleave', () => {
          this.isMouseOver = false;
          // Don't restart, just continue from current position
        });
      }
    }
  }
  
  setupResponsive() {
    const updateLayout = () => {
      const width = window.innerWidth;
      let currentSettings = { ...this.options };
      
      // Apply breakpoint settings
      Object.keys(this.options.breakpoints).forEach(breakpoint => {
        if (width >= parseInt(breakpoint)) {
          currentSettings = { ...currentSettings, ...this.options.breakpoints[breakpoint] };
        }
      });
      
      this.currentSettings = currentSettings;
      this.update();
    };
    
    updateLayout();
    window.addEventListener('resize', debounce(updateLayout, 250));
  }
  
  update() {
    const settings = this.currentSettings || this.options;
    const slideWidth = (100 / settings.slidesPerView);
    const spaceBetween = settings.spaceBetween || 0;
    
    this.slides.forEach((slide, index) => {
      slide.style.width = `calc(${slideWidth}% - ${spaceBetween * (settings.slidesPerView - 1) / settings.slidesPerView}px)`;
      if (index < this.slides.length - 1) {
        slide.style.marginRight = `${spaceBetween}px`;
      }
    });
    
    this.updateTransform();
    this.updateNavigation();
  }
  
  updateTransform() {
    if (this.isAutoplay && this.options.loop) {
      // For autoplay, transform is handled in startContinuousAutoplay
      return;
    } else {
      // Normal pagination
      const settings = this.currentSettings || this.options;
      const slideWidth = 100 / settings.slidesPerView;
      const translateX = -(this.currentIndex * slideWidth);
      this.wrapper.style.transform = `translateX(${translateX}%)`;
    }
  }
  
  updateNavigation() {
    const settings = this.currentSettings || this.options;
    const maxIndex = Math.max(0, this.slides.length - settings.slidesPerView);
    
    if (this.options.navigation.prevEl) {
      const prevBtn = document.querySelector(this.options.navigation.prevEl);
      if (prevBtn) {
        prevBtn.classList.toggle('swiper-button-disabled', this.currentIndex <= 0);
      }
    }
    
    if (this.options.navigation.nextEl) {
      const nextBtn = document.querySelector(this.options.navigation.nextEl);
      if (nextBtn) {
        nextBtn.classList.toggle('swiper-button-disabled', this.currentIndex >= maxIndex);
      }
    }
  }
  
  startContinuousAutoplay() {
    if (!this.isAutoplay) return;
    
    // Clone slides for seamless loop if not already done
    if (!this.hasClonedSlides) {
      this.createSeamlessLoop();
      this.hasClonedSlides = true;
    }
    
    if (!this.animationStarted) {
      this.currentTranslateX = 0;
      this.animationStarted = true;
    }
    
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const spaceBetween = this.currentSettings?.spaceBetween || this.options.spaceBetween || 0;
    const slideDistance = slideWidth + spaceBetween;
    const speed = 0.5; // pixels per frame
    
    const animate = () => {
      if (this.isAutoplay) {
        if (!this.isMouseOver) {
          this.currentTranslateX -= speed;
          
          // Reset position when we've moved one full slide width
          if (Math.abs(this.currentTranslateX) >= slideDistance) {
            this.currentTranslateX = 0;
            // Move first slide to end
            const firstSlide = this.wrapper.firstElementChild;
            if (firstSlide) {
              this.wrapper.appendChild(firstSlide);
            }
          }
          
          this.wrapper.style.transform = `translateX(${this.currentTranslateX}px)`;
        }
        // Continue animation even when paused, just don't update position
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }
  
  createSeamlessLoop() {
    // Clone enough slides to fill the viewport + extra for smooth scrolling
    const originalSlides = [...this.slides];
    const slidesToClone = Math.max(4, originalSlides.length);
    
    // Add clones to the end
    for (let i = 0; i < slidesToClone; i++) {
      const slideToClone = originalSlides[i % originalSlides.length];
      const clonedSlide = slideToClone.cloneNode(true);
      clonedSlide.classList.add('swiper-slide-duplicate');
      this.wrapper.appendChild(clonedSlide);
    }
    
    // Update slides array
    this.slides = [...this.element.querySelectorAll('.swiper-slide')];
  }
  
  pauseAutoplay() {
    // Just set the flag, animation will check it
    this.isMouseOver = true;
  }
  
  slideNext() {
    const settings = this.currentSettings || this.options;
    const maxIndex = Math.max(0, this.slides.length - settings.slidesPerView);
    
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateTransform();
      this.updateNavigation();
    }
  }
  
  slidePrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateTransform();
      this.updateNavigation();
    }
  }
  
  removeSlide(index) {
    if (index >= 0 && index < this.slides.length) {
      const slide = this.slides[index];
      if (slide && slide.parentNode) {
        slide.remove();
        this.slides = [...this.element.querySelectorAll('.swiper-slide')];
        this.update();
      }
    }
  }
  
  addSlide(index, slideElement) {
    if (slideElement) {
      const slides = [...this.element.querySelectorAll('.swiper-slide')];
      if (index <= slides.length) {
        if (index === slides.length) {
          this.wrapper.appendChild(slideElement);
        } else {
          this.wrapper.insertBefore(slideElement, slides[index]);
        }
        this.slides = [...this.element.querySelectorAll('.swiper-slide')];
        this.update();
      }
    }
  }
}

// Custom element for social media highlights slider
if (!customElements.get("social-media-highlights-slider")) {
  class SocialMediaHighlightsSlider extends HTMLElement {
    constructor() {
      super();
      // Store Content Card before initialization
      this.slideContent = this.querySelector('.social-media-highlights__content-card');
      
      // Swiper options & init
      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};
      const swiperLayout = this.getAttribute("data-swiper-layout") || "default";
      this.sliderItem = this.querySelector('.swiper');
      
      this.sliderOptions = {
        slidesPerView: 1,
        spaceBetween: swiperOptions.spaceBetweenMobile || 10,
        breakpoints: {
          750: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 20
          },
          1100: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 20
          }
        },
        on: {
          afterInit: () => {
            if (this.querySelector(".swiper-smh")) {
              this.querySelector(".swiper-smh").removeAttribute("style");
            }
          }
        }
      };
      
      if (swiperOptions.autoplaySpeed != 0 && swiperLayout === "default") {
        this.sliderOptions = {
          speed: swiperOptions.autoplaySpeed || 5000,
          autoplay: {
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          },
          ...this.sliderOptions
        };
      }
      
      if (swiperLayout === "default") {
        this.sliderOptions = {
          loop: true,
          ...this.sliderOptions
        };
      }
      
      if (swiperLayout === "stacked") {
        this.sliderOptions = {
          navigation: {
            prevEl: ".swiper-button--prev",
            nextEl: ".swiper-button--next"
          },
          ...this.sliderOptions
        };
      }
      
      // Handle Shopify design mode
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
      }
    }
    
    connectedCallback() {
      this.init();
    }
    
    init() {
      if (!this.sliderItem) return;
      
      this.swiper = new SimpleSwiper(this.sliderItem, this.sliderOptions);
      this.toggleSlideContentCard();
    }
    
    // Hide / Show Content Card
    toggleSlideContentCard() {
      let swiperInitialized = false;
      
      if (window.innerWidth < 750 && this.slideContent) {
        this.swiper.removeSlide(2);
      }
      
      const updateSlideContent = () => {
        if (swiperInitialized && this.slideContent) {
          if (window.innerWidth < 750) {
            this.swiper.removeSlide(2);
          } else {
            this.swiper.addSlide(2, this.slideContent);
          }
        }
      };
      
      updateSlideContent();
      
      window.addEventListener(
        "resize",
        debounce(() => {
          updateSlideContent();
        }, 200)
      );
      
      this.addEventListener("swiper:init", () => {
        swiperInitialized = true;
      });
    }
  }
  
  customElements.define("social-media-highlights-slider", SocialMediaHighlightsSlider);
}